import { useEffect, useMemo, useState } from 'react';
import { Megaphone, ArrowRight } from 'lucide-react';
import {
  useBroadcastPreview,
  useMembershipBroadcast,
  useMembershipTypes,
} from '../../queries';

type Channel = 'SMS' | 'PUSH';

const MAX_MESSAGE_LENGTH = 300;

const BroadcastComposer = () => {
  const typesQuery = useMembershipTypes(true);
  const previewMutation = useBroadcastPreview();
  const sendMutation = useMembershipBroadcast();

  const [audience, setAudience] = useState('all');
  const [message, setMessage] = useState('');
  const [channels, setChannels] = useState<Channel[]>(['SMS']);
  const [previewed, setPreviewed] = useState<{ count: number; sample: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const tiers = typesQuery.data?.items ?? [];

  // Invalidate any prior preview if the audience changes — keeps the count
  // honest before the merchant hits "Send".
  useEffect(() => {
    setPreviewed(null);
  }, [audience]);

  const trimmedMessage = message.trim();
  const canPreview = audience.trim().length > 0 && !previewMutation.isPending;
  const canSend = useMemo(() => {
    if (sendMutation.isPending) return false;
    if (!previewed || previewed.count === 0) return false;
    if (trimmedMessage.length === 0 || trimmedMessage.length > MAX_MESSAGE_LENGTH) return false;
    if (channels.length === 0) return false;
    return true;
  }, [previewed, trimmedMessage, channels, sendMutation.isPending]);

  const toggleChannel = (channel: Channel) => {
    setChannels((current) =>
      current.includes(channel)
        ? current.filter((c) => c !== channel)
        : [...current, channel]
    );
  };

  const handlePreview = () => {
    setError(null);
    setFeedback(null);
    previewMutation.mutate(audience, {
      onSuccess: (data) =>
        setPreviewed({ count: data.recipientCount, sample: data.sampleRecipientNames }),
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Could not load audience preview.';
        setError(msg);
      },
    });
  };

  const handleSend = () => {
    setError(null);
    setFeedback(null);
    sendMutation.mutate(
      { audience, message: trimmedMessage, channels },
      {
        onSuccess: (response) => {
          setFeedback(`Queued for ${response.queued} member${response.queued === 1 ? '' : 's'}.`);
          setMessage('');
          setPreviewed(null);
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Could not queue this broadcast.';
          setError(msg);
        },
      }
    );
  };

  return (
    <article className="actions-panel" data-testid="broadcast-composer">
      <header className="actions-panel__header">
        <span className="actions-panel__icon" aria-hidden="true">
          <Megaphone size={16} />
        </span>
        <div className="actions-panel__title-block">
          <h3 className="actions-panel__title">Broadcast</h3>
          <p className="actions-panel__subtitle">
            Preview the audience first; nothing dispatches until you press Send.
          </p>
        </div>
      </header>

      <div className="broadcast-composer__form">
        <label className="broadcast-composer__field">
          <span className="broadcast-composer__label">AUDIENCE</span>
          <select
            className="broadcast-composer__select"
            value={audience}
            onChange={(event) => setAudience(event.target.value)}
            data-testid="broadcast-audience"
          >
            <option value="all">All members</option>
            {tiers.map((tier) => (
              <option key={tier.membershipTypeUuid} value={tier.membershipTypeUuid}>
                {tier.name}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="broadcast-composer__channels">
          <legend className="broadcast-composer__label">CHANNELS</legend>
          {(['SMS', 'PUSH'] as const).map((channel) => (
            <label
              key={channel}
              className={`broadcast-composer__channel${channels.includes(channel) ? ' broadcast-composer__channel--active' : ''}`}
            >
              <input
                type="checkbox"
                checked={channels.includes(channel)}
                onChange={() => toggleChannel(channel)}
                data-testid={`broadcast-channel-${channel.toLowerCase()}`}
              />
              {channel}
            </label>
          ))}
        </fieldset>

        <label className="broadcast-composer__field">
          <span className="broadcast-composer__label">
            MESSAGE
            <span className="broadcast-composer__counter">{trimmedMessage.length}/{MAX_MESSAGE_LENGTH}</span>
          </span>
          <textarea
            className="broadcast-composer__textarea"
            placeholder="Your points expire soon — visit us this week to redeem them."
            value={message}
            onChange={(event) => setMessage(event.target.value.slice(0, MAX_MESSAGE_LENGTH))}
            rows={3}
            data-testid="broadcast-message"
          />
        </label>

        {previewed ? (
          <div className="broadcast-composer__preview" data-testid="broadcast-preview-summary">
            <div className="broadcast-composer__preview-count">
              <strong>{previewed.count}</strong> recipient{previewed.count === 1 ? '' : 's'}
            </div>
            {previewed.sample.length > 0 ? (
              <div className="broadcast-composer__preview-sample">
                Sample: {previewed.sample.slice(0, 3).join(', ')}
                {previewed.sample.length > 3 ? '…' : ''}
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <div className="broadcast-composer__error" data-testid="broadcast-error">{error}</div>
        ) : null}
        {feedback ? (
          <div className="broadcast-composer__feedback" data-testid="broadcast-feedback">{feedback}</div>
        ) : null}

        <footer className="broadcast-composer__footer">
          <button
            type="button"
            className="broadcast-composer__preview-btn"
            onClick={handlePreview}
            disabled={!canPreview}
            data-testid="broadcast-preview-button"
          >
            {previewMutation.isPending ? 'Previewing…' : 'Preview audience'}
            <ArrowRight size={14} />
          </button>
          <button
            type="button"
            className="broadcast-composer__send-btn"
            onClick={handleSend}
            disabled={!canSend}
            data-testid="broadcast-send-button"
          >
            {sendMutation.isPending ? 'Sending…' : 'Send broadcast'}
          </button>
        </footer>
      </div>
    </article>
  );
};

export default BroadcastComposer;
